import { cn } from '@lib/utils';
import Image, { ImageProps } from 'next/image';
import React, { ReactNode, useMemo } from 'react';
import cardDefaultImg from '@images/card-default.webp';
import { Link } from '@/navigation';
import { TrackedAnchor } from '../ancher/tracked-anchor';
import { TrackedEventOptions } from '@/constants';

interface Props {
  className?: string;
  children?: ReactNode;
}
type CardProps = Props & {
  href: string;
  src?: ImageProps['src'];
  alt?: ImageProps['alt'];
  options?: TrackedEventOptions;
};

export const Card = ({
  className,
  children,
  href,
  src = cardDefaultImg,
  alt,
  options,
}: CardProps) => {
  const classNames = cn(
    'w-[370px] h-[470px] rounded-[20px] relative block p-9 group text-white',
    className,
  );

  const CardImage = useMemo(
    () => (
      <Image
        src={src}
        alt={alt ?? 'card image'}
        fill
        className="rounded-[20px] object-cover"
      />
    ),
    [src, alt],
  );

  if (options?.gtagOptions?.addGtag) {
    return (
      <TrackedAnchor
        className={classNames}
        href={href}
        prefetch={false}
        label={options.gtagOptions.label}
        position={options.gtagOptions.position}
      >
        {CardImage}
        {children}
      </TrackedAnchor>
    );
  }

  return (
    <Link href={href} className={classNames} prefetch={false}>
      {CardImage}
      {children}
    </Link>
  );
};

const Title = ({ children, className }: Props) => {
  const baseClassName =
    'relative text-2xl font-semibold tracking-[-0.46px] group-hover:underline break-keep';
  return <div className={cn(baseClassName, className)}>{children}</div>;
};
const Info = ({ children, className }: Props) => {
  const baseClassName =
    'relative text-xs leading-[18px] tracking-[0.02px] font-semibold';
  return <div className={cn(baseClassName, className)}>{children}</div>;
};

Card.Title = Title;
Card.Info = Info;
